import { useRef, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import jsQR from "jsqr";
import {
  ChevronLeft,
  CheckCircle2,
  QrCode,
  Camera,
  TriangleAlert,
  User,
  Phone,
  Upload,
  CircleCheck,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";
import ApiService from "../../../components/Api_services";
import {
  PHONE_COUNTRY_OPTIONS,
  inferCountryFromName,
  splitPhoneInput,
  isValidRegionalPhone,
  detectCountryFromFullNumber,
} from "../warrantyPhoneUtils";
import MultiPurpose from "../aftersales_multipurpose/MultiPurpose";
import RegisteredReceived from "../registeredreceived/RegisteredReceived";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import { useLanguage } from "../LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
  parseQrSerialPayload,
} from "../serialTypeUtils";

const NewInstallation = () => {
  const skipCameraRef = useRef(false);
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const photoInputRef = useRef(null);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const pinCode = searchParams.get("pinCode");

  const [customerData, setCustomerData] = useState("");
  const [warrantyInfo, setWarrantyInfo] = useState({
    warrantyitemlist: "",
    salesorder: "",
    custname: "",
    custtelno: "",
    emailadd: "",
    country: "",
    state: "",
    city: "",
    postcode: "",
    street: "",
    invoiceno: "",
    purchasedate: "",
  });
  const [loading, setLoading] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [cleanPhoneNumber, setCleanPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("BN");
  const [errors, setErrors] = useState({
    custname: "",
    custtelno: "",
    photoattach: "",
  });
  const [showRegistered, setShowRegistered] = useState(false);
  const [responseStatus, setResponseStatus] = useState(null);
  const [showUpdatePIN, setShowUpdatePIN] = useState(false);

  // qrscanner states
  const cameraStreamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const uploadInitializedRef = useRef(false);
  // const [sn2, setSn2] = useState("");
  // const [duplicateError, setDuplicateError] = useState(false);

  const [scannedUnits, setScannedUnits] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [scanError, setScanError] = useState("");
  const [scanSuccess, setScanSuccess] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const scanCooldownRef = useRef(false);
  const scannedUnitsRef = useRef([]);
  const [nextBtnError, setNextBtnError] = useState("");
  const [checkingUnits, setCheckingUnits] = useState(false);
  const [unitsExpanded, setUnitsExpanded] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    const fetchRegisterStatus = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.checkRegisterStatus(
          serialNo,
          serialType,
        );

        if (response) {
          if (response.status === 200) {
            skipCameraRef.current = true;

            if (response.message === "Success") {
              navigate(
                `/aftersalesservice/menucenter${buildAfterSalesQuery(searchParams)}`,
              );
            } else if (
              response.message === "Success, allowed to update PIN code"
            ) {
              setShowUpdatePIN(true);
            }
          } else if (
            response.message ===
            "Serial Number Registered (Installation), But In Verification Process"
          ) {
            skipCameraRef.current = true;
            setResponseStatus("newinstall");
            setShowRegistered(true);
          } else if (
            response.message ===
            "Serial Number Registered, But In Verification Process"
          ) {
            skipCameraRef.current = true;
            setShowRegistered(true);
          }
        }
      } catch (error) {
        console.error("Error fetching register status:", error);
      }
    };

    fetchRegisterStatus();
  }, [serialNoForApi, serialType]);

  useEffect(() => {
    const fetchWarrantyInfo = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.getWarrantyInfo(serialNo, serialType);

        if (response && response.status === 200) {
          const items = response.data?.warranty_item_list || [];
          // ensure each item has serial_no
          const normalizedItems = (Array.isArray(items) ? items : []).map(
            (it) => {
              const { item_warranty_period, ...rest } = it || {};
              return {
                ...rest, // rest no longer contains item_warranty_period
                // map item_warranty_period -> warranty_day (ensure numeric)
                warranty_day:
                  rest?.warranty_day ??
                  (item_warranty_period != null
                    ? Number(item_warranty_period)
                    : 0),
              };
            },
          );

          const customer = response.data?.customer_data || null;
          const phoneRaw = customer?.phone_no || "";
          const defaultCountry =
            inferCountryFromName(customer?.country) || "BN";
          const normalized = splitPhoneInput(phoneRaw, defaultCountry);
          setPhoneCountry(normalized.countryId || defaultCountry);
          setCleanPhoneNumber(normalized.digit_only);

          const updateFields = {
            warrantyitemlist: normalizedItems,
            salesorder: customer?.sales_order || "",
            custname: customer?.receiver_name || "",
            custtelno: normalized.nationalDisplay || normalized.national || "",
            country: customer?.country || "",
            state: customer?.province || "",
            city: customer?.city || "",
            postcode: customer?.zip_code || "",
            street: customer?.address || "",
            invoiceno: customer?.shopee_order_id || "",
            purchasedate: customer?.purchase_date || "",
          };

          setWarrantyInfo((prev) => ({ ...prev, ...updateFields }));
          setCustomerData(response.data?.customer_data);
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
      }
    };

    fetchWarrantyInfo();
  }, [serialNoForApi, serialType]);

  // Determine serial labels based on scanned SN type
  const matchedItem = (warrantyInfo.warrantyitemlist || []).find(
    (item) => item.serial_no === sn,
  );
  const isOutdoor = matchedItem?.type?.toUpperCase() === "OUTDOOR";
  const serial_label1 = isOutdoor ? trans.outdoor_unit : trans.indoor_unit;
  const serial_label2 = isOutdoor ? trans.indoor_unit : trans.outdoor_unit;

  const allUnits = [
    {
      serial_no: sn,
      type: isOutdoor ? "OUTDOOR" : "INDOOR",
      auto: true,
      item_code: matchedItem?.item_code,
    },
    ...scannedUnits,
  ];
  const indoorCount = allUnits.filter((u) => u.type === "INDOOR").length;
  const outdoorCount = allUnits.filter((u) => u.type === "OUTDOOR").length;
  const countsMatch =
    indoorCount > 0 && outdoorCount > 0 && indoorCount === outdoorCount;
  const canProceed = countsMatch && confirmChecked;

  const handlePhoneInput = (value) => {
    const split = splitPhoneInput(value, phoneCountry);
    const digits = (value || "").replace(/[^\d]/g, "");
    if (detectCountryFromFullNumber(digits)) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || "",
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handlePhoneBlur = (value) => {
    const trimmed = value.trim();
    if (trimmed === "") return;

    const split = splitPhoneInput(trimmed, phoneCountry);
    if (detectCountryFromFullNumber(trimmed.replace(/[^\d]/g, ""))) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || trimmed,
    }));
  };

  const handlePhoneCountryChange = (countryId) => {
    setPhoneCountry(countryId);
    if (!warrantyInfo.custtelno?.trim()) return;

    const split = splitPhoneInput(warrantyInfo.custtelno, countryId);
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || prev.custtelno,
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    setNotifMessage("");

    let newErrors = { custname: "", custtelno: "", photoattach: "" };
    let hasError = false;

    if (!pinCode) {
      setNotifMessage("Pin code missing");
      setShowMessage(true);
      setLoading(false);
      return;
    }

    if (!warrantyInfo.custname) {
      newErrors.custname = "Please enter buyer name";
      hasError = true;
    }

    if (!warrantyInfo.custtelno) {
      newErrors.custtelno = "Please enter mobile number";
      hasError = true;
    } else if (!isValidRegionalPhone(warrantyInfo.custtelno, phoneCountry)) {
      newErrors.custtelno = "Invalid mobile number format for selected country";
      hasError = true;
    }

    // if (!uploadedPhotos) {
    //   newErrors.photoattach = "Please upload at least one install photo";
    //   hasError = true;
    // }

    setErrors(newErrors);

    if (hasError) {
      setLoading(false);
      return;
    }

    const submitPhone =
      cleanPhoneNumber ||
      splitPhoneInput(warrantyInfo.custtelno, phoneCountry).digit_only;

    const getBaseItemCode = (itemCode) => {
      if (!itemCode) return "UNKNOWN";
      return itemCode.replace(/\((I|O)\)\s*$/i, "").trim();
    };

    const groups = {};
    allUnits.forEach((u) => {
      const key = getBaseItemCode(u.item_code);
      if (!groups[key]) groups[key] = { indoor: [], outdoor: [] };
      if (u.type === "INDOOR") {
        groups[key].indoor.push(u);
      } else {
        groups[key].outdoor.push(u);
      }
    });

    const sets = [];
    Object.values(groups).forEach((g) => {
      const pairCount = Math.min(g.indoor.length, g.outdoor.length);
      for (let i = 0; i < pairCount; i++) {
        sets.push([g.indoor[i], g.outdoor[i]]);
      }
    });

    if (sets.length === 0) {
      setNotifMessage("Unable to pair indoor/outdoor units.");
      setShowMessage(true);
      setLoading(false);
      return;
    }

    // Prepare warranty_item_list with only required fields
    // const warranty_item_list = allUnits.map((unit) => {
    //   const mappedItem = { serial_no: unit.serial_no };

    //   const warrantyMeta = warrantyInfo.warrantyitemlist.find(
    //     (item) => item.item_code === unit.item_code
    //   );

    //   // Only include warranty if it exists and has data
    //   // if (item.warranty && item.warranty.length > 0) {
    //   //   mappedItem.warranty = item.warranty.map((w) => ({
    //   if (warrantyMeta?.warranty && warrantyMeta.warranty.length > 0) {
    //     mappedItem.warranty = warrantyMeta.warranty.map((w) => ({
    //       name: w.name,
    //       duration: w.duration,
    //     }));
    //   }

    //   return mappedItem;
    // });

    // const formData = new FormData();
    // formData.append("pin_code", pinCode);
    // formData.append("name", warrantyInfo.custname);
    // formData.append("phone", submitPhone);
    // formData.append("phone_country", phoneCountry);
    // formData.append("email", warrantyInfo.emailadd || "");
    // formData.append("warranty_item_list", JSON.stringify(warranty_item_list));
    // if (uploadedPhotos.length > 0) {
    //   uploadedPhotos.forEach((item) => {
    //     if (item.file) {
    //       formData.append("install_photo[]", item.file);
    //     }
    //   });
    // }

    // try {
    //   const response = await ApiService.newRegisterWarranty(formData);
    //   if (response && response.status === 200) {
    //     navigate(`/aftersalesservice?sn=${btoa(getSerialNo())}`);
    //   } else {
    //     console.error("Error submitting registration:", response);
    //     // Handle API error message if available
    //     const errorMessage =
    //       response?.message || "Failed to submit registration.";
    //     setNotifMessage(errorMessage);
    //     setShowMessage(true);
    //   }
    // }
    try {
      for (const set of sets) {
        const warranty_item_list = set.map((unit) => {
          const mappedItem = { serial_no: unit.serial_no };
          const warrantyMeta = warrantyInfo.warrantyitemlist.find(
            (item) => item.item_code === unit.item_code,
          );
          if (warrantyMeta?.warranty && warrantyMeta.warranty.length > 0) {
            mappedItem.warranty = warrantyMeta.warranty.map((w) => ({
              name: w.name,
              duration: w.duration,
            }));
          }
          return mappedItem;
        });

        const formData = new FormData();
        formData.append("pin_code", pinCode);
        formData.append("name", warrantyInfo.custname);
        formData.append("phone", submitPhone);
        formData.append("phone_country", phoneCountry);
        formData.append("email", warrantyInfo.emailadd || "");
        formData.append(
          "warranty_item_list",
          JSON.stringify(warranty_item_list),
        );
        formData.append("serial_type", serialType);
        if (uploadedPhotos.length > 0) {
          uploadedPhotos.forEach((item) => {
            if (item.file) formData.append("install_photo[]", item.file);
          });
        }

        // 一组存完再存下一组
        const response = await ApiService.registerWarranty(formData);
        if (!response || response.status !== 200) {
          const errorMessage =
            response?.message || "Failed to submit registration.";
          setNotifMessage(errorMessage);
          setShowMessage(true);
          setLoading(false);
          return;
        }
      }

      navigate(`/aftersalesservice${buildAfterSalesQuery(searchParams)}`);
    } catch (error) {
      console.error("Error submitting registration:", error);
      // Handle different types of errors
      let errorMessage = "Failed to submit registration.";

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setNotifMessage(errorMessage);
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePIN = async () => {
    const serialNo = serialNoForApi;

    if (!pinCode) {
      setNotifMessage("Pin code missing");
      setShowMessage(true);
      return;
    }

    try {
      const response = await ApiService.updatePIN(
        serialNo,
        pinCode,
        serialType,
      );

      if (response) {
        if (response.status === 200) {
          navigate(
            `/aftersalesservice/menucenter${buildAfterSalesQuery(searchParams)}`,
          );
        } else {
          const errorMessage =
            response?.message || "Failed to update PIN code.";
          setNotifMessage(errorMessage);
          setShowMessage(true);
        }
      }
    } catch (error) {
      console.error("Error updating PIN code:", error);

      const errorMessage = error?.message || "Failed to update PIN code.";
      setNotifMessage(errorMessage);
      setShowMessage(true);
    }
  };

  // ── qrScanner ───────────────────────────────────────────────────────────
  // QR scanner code commented out per request - keep for reference
  const stopCameraScanner = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    const qrReader = document.getElementById("qr-reader");
    if (qrReader) qrReader.innerHTML = "";
  };

  const initializeCameraScanner = () => {
    // Release any previous session before opening a new one
    stopCameraScanner();

    const video = document.createElement("video");
    video.setAttribute("playsinline", true);
    video.style.width = "100%";
    // video.style.height = "250px";

    const qrReader = document.getElementById("qr-reader");
    qrReader.innerHTML = "";
    qrReader.appendChild(video);

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        // Scanner closed/restarted while waiting for permission — release this stream
        if (cameraStreamRef.current || !video.isConnected) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        cameraStreamRef.current = stream;
        video.srcObject = stream;
        video.play();

        scanIntervalRef.current = setInterval(() => {
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height,
            );
            const code = jsQR(imageData.data, canvas.width, canvas.height);
            if (code) {
              processScannedQR(code.data);
            }
          }
        }, 500);
      })
      .catch((err) => {
        console.error("Camera error:", err);
        alert("Unable to access camera. Please try uploading a photo instead.");
      });
  };

  const scanQRFromImage = (dataUrl) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, canvas.width, canvas.height);
      if (code) {
        processScannedQR(code.data);
      } else {
        alert(
          "No QR Code Found. Please try a clearer image or use the camera scanner.",
        );
      }
    };
    img.onerror = () => alert("Failed to load image.");
    img.src = dataUrl;
  };

  const processScannedQR = async (qrData) => {
    if (scanCooldownRef.current) return;
    try {
      const { apiSerial: decodedSerial, serialType: scannedSerialType } =
        parseQrSerialPayload(qrData);

      if (!decodedSerial) {
        setScanError("Invalid QR code data");
        return;
      }

      const isDuplicate =
        decodedSerial === serialNoForApi ||
        scannedUnitsRef.current.some((u) => u.serial_no === decodedSerial);

      if (isDuplicate) {
        setScanError(trans.new_install_unit_2_error);
        return;
      }

      scanCooldownRef.current = true;
      setScanError("");

      const response = await ApiService.getWarrantyInfo(
        decodedSerial,
        scannedSerialType,
      );

      if (!response || response.status !== 200) {
        setScanError(response?.message || "Serial number not found");
        scanCooldownRef.current = false;
        return;
      }

      const items = response.data?.warranty_item_list || [];
      const matched = items.find((it) => it.serial_no === decodedSerial);

      if (!matched) {
        setScanError("Serial number not found in item list");
        setShowCamera(true);
        return;
      }

      const unitType =
        matched.type?.toUpperCase() === "OUTDOOR" ? "OUTDOOR" : "INDOOR";

      setScannedUnits((prev) => [
        ...prev,
        {
          serial_no: decodedSerial,
          type: unitType,
          item_code: matched.item_code,
        },
      ]);
      setScanSuccess(true);

      setTimeout(() => {
        scanCooldownRef.current = false;
      }, 3000);
    } catch (error) {
      console.error("Error processing QR code:", error);
      alert("Error processing QR code data.");
      setShowCamera(true);
    }
  };

  // useEffect(() => {
  //   let timer;
  //   if (step === 1) {
  //     timer = setTimeout(() => {
  //       if (!skipCameraRef.current) {
  //         initializeCameraScanner();
  //       }
  //     }, 3000);
  //   }
  //   return () => {
  //     clearTimeout(timer);
  //     stopCameraScanner();
  //   };
  // }, [step]);

  useEffect(() => {
    if (showCamera) {
      initializeCameraScanner();
    } else {
      stopCameraScanner();
    }
    return () => stopCameraScanner();
  }, [showCamera]);

  useEffect(() => {
    scannedUnitsRef.current = scannedUnits;
  }, [scannedUnits]);

  useEffect(() => {
    if (!scanError) return;
    const timer = setTimeout(() => setScanError(""), 3000);
    return () => clearTimeout(timer);
  }, [scanError]);

  useEffect(() => {
    if (!scanSuccess) return;
    const timer = setTimeout(() => setScanSuccess(false), 3000);
    return () => clearTimeout(timer);
  }, [scanSuccess]);

  const handleScanButtonClick = () => {
    setScanError("");
    setScanSuccess(false);
    setNextBtnError("");
    setShowCamera(true);
  };

  const removeScannedUnit = (idx) => {
    setScannedUnits((prev) => prev.filter((_, i) => i !== idx - 1));
    setNextBtnError("");
  };

  const handleNextStep = async () => {
    setNextBtnError("");
    setCheckingUnits(true);
    try {
      const unitsPayload = allUnits.map((u) => ({
        serial_no: u.serial_no,
        type: u.type,
      }));
      const response = await ApiService.checkUnitsBundle(unitsPayload);
      if (response && response.status === 200) {
        setShowCamera(false);
        setStep(2);
      } else {
        setNextBtnError(response?.message || "Unable to verify scanned units.");
      }
    } catch (error) {
      console.error("Error checking units:", error);
      setNextBtnError("Unable to verify scanned units.");
    } finally {
      setCheckingUnits(false);
    }
  };

  return (
    <AfterSalesLayout>
      <div className="as-new-install-top">
        <div style={{ width: "359.74px" }}>
          <button
            className="as-new-install-cancel"
            onClick={() =>
              navigate(
                `/aftersalesservice${buildAfterSalesQuery(searchParams)}`,
              )
            }
          >
            <ChevronLeft size={20} />
            {trans.cancel}
          </button>
          <div className="as-new-install-title">{trans.new_install}</div>
          <div className="as-new-install-subtitle">
            {trans.new_install_step}
            {step}
            {trans.new_install_step_of}
          </div>
          <div className="as-new-install-progress">
            <div
              className={`as-new-install-progress-segment ${step >= 1 ? "as-active" : ""}`}
            />
            <div
              className={`as-new-install-progress-segment ${step >= 2 ? "as-active" : ""}`}
            />
          </div>
        </div>
      </div>

      <div className="as-new-install-bottom">
        {step === 1 ? (
          <div className="as-new-install-main">
            <div className="as-new-install-card-camera">
              {showCamera && (
                <div
                  id="camera-section"
                  className="as-scanner-section as-active"
                >
                  <div id="qr-reader"></div>
                </div>
              )}

              {/* <div className="as-new-install-scan-icon">
                
              </div> */}
              <div className="as-new-install-scan-desc">
                {trans.new_install_unit_2_sub}
              </div>

              <div className="as-new-install-card-bottom">
                <button
                  type="button"
                  className="as-new-install-scan-title"
                  onClick={handleScanButtonClick}
                >
                  <QrCode size={18} />
                  {trans.new_install_unit_2}
                </button>

                {scanError && (
                  <div className="as-new-install-duplicate-error">
                    <TriangleAlert size={20} />
                    <span>{scanError}</span>
                  </div>
                )}

                {scanSuccess && (
                  <div className="as-new-install-scan-success">
                    <CheckCircle2 size={16} />
                    <span>{trans.new_install_scan_success}</span>
                  </div>
                )}
              </div>
            </div>

            {/* <div className="as-new-install-card">
              <div className="as-new-install-card-label">
                {trans.new_install_unit_1}
              </div>
              <div className="as-new-install-card-sn">
                <div className="as-new-install-sn-icon">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <div className="as-new-install-sn-name">
                    {isOutdoor
                      ? trans.new_install_ac_outdoor
                      : trans.new_install_ac_indoor}
                  </div>
                  <div className="as-new-install-sn-serial">
                    S/N: {getSerialNo()}
                  </div>
                </div>
              </div>
            </div> */}

            <div className="as-new-install-card-scanned">
              <div className="as-new-install-card-label">
                <span
                  style={{
                    fontSize: "14px",
                    color: "#404040",
                    fontWeight: 600,
                  }}
                >
                  {trans.new_install_scanned_unit}
                </span>
                <span style={{ fontSize: "12px", color: "#a1a1a1" }}>
                  {allUnits.length} {trans.new_install_total}
                </span>
              </div>
              {allUnits.map((unit, idx) => (
                <div
                  key={unit.serial_no}
                  className="as-new-install-card-sn"
                  style={{ position: "relative" }}
                >
                  <div
                    className="as-new-install-sn-icon"
                    style={{
                      background:
                        unit.type === "OUTDOOR" ? "#fe9a00" : "#0b5ed7",
                    }}
                  >
                    {/* <CheckCircle2 size={20} /> */}
                  </div>
                  <div>
                    <div className="as-new-install-sn-name">
                      {unit.item_code}
                    </div>
                    <div className="as-new-install-sn-serial">
                      S/N: {unit.serial_no}
                    </div>
                    <div
                      className="as-new-install-type"
                      style={{
                        color: unit.type === "OUTDOOR" ? "#fe9a00" : "#0b5ed7",
                      }}
                    >
                      {unit.type === "OUTDOOR"
                        ? trans.outdoor_unit
                        : trans.indoor_unit}
                      {unit.auto ? trans.new_install_auto_detect : ""}
                    </div>
                  </div>
                  {!unit.auto && (
                    <button
                      className="as-new-install-photo-remove"
                      onClick={() => removeScannedUnit(idx)}
                      style={{ marginLeft: "auto" }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {allUnits.length > 1 && !countsMatch && (
              <div className="as-new-install-warning-error">
                <TriangleAlert size={20} />
                <span>
                  {indoorCount} {trans.indoor_unit} · {outdoorCount}{" "}
                  {trans.outdoor_unit} {trans.new_install_unit_2_error2}
                </span>
              </div>
            )}

            {countsMatch && (
              <label
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-start",
                  fontSize: "13px",
                }}
              >
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
                {trans.new_install_note}
              </label>
            )}

            {nextBtnError && (
              <div className="as-new-install-duplicate-error">
                <TriangleAlert size={14} />
                <span>{nextBtnError}</span>
              </div>
            )}

            <button
              type="button"
              className="as-new-install-next-btn"
              disabled={!canProceed || checkingUnits}
              onClick={handleNextStep}
            >
              {checkingUnits ? "Checking..." : trans.new_install_next_btn}
            </button>
          </div>
        ) : step === 2 ? (
          <div className="as-new-install-main">
            {/* <div className="as-new-install-card as-new-install-units-registered">
              <div className="as-new-install-units-title">
                <CheckCircle2 size={20} color="#00a63e" />
                <span>{trans.units_reg}</span>
              </div>
              <div
                className="as-new-install-units-row"
                style={{ marginBottom: "8px" }}
              >
                <span className="as-new-install-units-label">{serial_label1}</span>
                <span className="as-new-install-units-value">{getSerialNo()}</span>
              </div>
              <div className="as-new-install-units-row">
                <span className="as-new-install-units-label">{serial_label2}</span>
                <span className="as-new-install-units-value">{sn2}</span>
              </div>
            </div> */}

            <div className="as-new-install-card as-new-install-units-registered">
              <button
                type="button"
                className="as-new-install-units-header"
                onClick={() => setUnitsExpanded((prev) => !prev)}
              >
                <div className="as-new-install-units-title">
                  <CheckCircle2 size={20} color="#00a63e" />
                  <span>
                    {indoorCount} {trans.new_install_set_register}
                  </span>
                </div>
                {/* <div className="as-new-install-units-header-right">
                  <span className="as-new-install-units-count">{allUnits.length} {trans.units}</span>
                  {unitsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div> */}
              </button>

              {/* {unitsExpanded && (
                <div className="as-new-install-units-columns">
                  <div className="as-new-install-units-column">
                    <div className="as-new-install-units-column-label">
                      <span className="as-new-install-units-dot as-indoor" />
                      {trans.indoor_unit}
                    </div>
                    {allUnits
                      .filter((u) => u.type === "INDOOR")
                      .map((unit) => (
                        <div className="as-new-install-units-value" key={unit.serial_no}>
                          {unit.serial_no}
                        </div>
                      ))}
                  </div>

                  <div className="as-new-install-units-column">
                    <div className="as-new-install-units-column-label">
                      <span className="as-new-install-units-dot as-outdoor" />
                      {trans.outdoor_unit}
                    </div>
                    {allUnits
                      .filter((u) => u.type === "OUTDOOR")
                      .map((unit) => (
                        <div className="as-new-install-units-value" key={unit.serial_no}>
                          {unit.serial_no}
                        </div>
                      ))}
                  </div>
                </div>
              )} */}
            </div>

            <div className="as-new-install-card">
              <div className="as-new-install-section-title">
                {trans.new_install_cust_details}
              </div>

              <div className="as-new-install-field">
                <label className="as-new-install-field-label">
                  <User size={16} />
                  {trans.new_install_cust_name}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  className="as-new-install-input"
                  type="text"
                  placeholder="Full name of customer"
                  value={warrantyInfo.custname}
                  onChange={(e) => {
                    setWarrantyInfo((p) => ({
                      ...p,
                      custname: e.target.value,
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      custname: "",
                    }));
                  }}
                />
                {errors.custname && (
                  <div className="as-error">{errors.custname}</div>
                )}
              </div>

              <div className="as-new-install-field">
                <label className="as-new-install-field-label">
                  <Phone size={16} />
                  {trans.new_install_contact_no}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <div className="as-csn_phone-row">
                  <select
                    className="as-csn_input-box as-csn_phone-country-select"
                    value={phoneCountry}
                    onChange={(e) => handlePhoneCountryChange(e.target.value)}
                    aria-label="Country code"
                  >
                    {PHONE_COUNTRY_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.dial}
                      </option>
                    ))}
                  </select>
                  <input
                    className="as-new-install-input"
                    type="text"
                    placeholder="e.g. 012-345 6789"
                    value={warrantyInfo.custtelno}
                    onChange={(e) => handlePhoneInput(e.target.value)}
                    onBlur={(e) => handlePhoneBlur(e.target.value)}
                  />
                </div>
                {errors.custtelno && (
                  <div className="as-error">{errors.custtelno}</div>
                )}
              </div>

              {/* <div className="as-new-install-field" style={{ margin: "0" }}>
                <label className="as-new-install-field-label">
                  <Camera size={16} />
                  {trans.new_install_photos}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <div className="as-new-install-field-sublabel">
                  {trans.new_install_photos_sub}
                </div>
                <div className="as-new-install-photos-slots">
                  {uploadedPhotos.map((item, idx) => (
                    <div key={idx} className="as-new-install-photo-thumb">
                      <img src={item.src} alt={`upload-${idx}`} />
                      <button
                        className="as-new-install-photo-remove"
                        onClick={() =>
                          setUploadedPhotos((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {uploadedPhotos.length < 3 && (
                    <div
                      className="as-new-install-photo-add"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Upload size={20} color="#6b7280" />
                      <span>{trans.add}</span>
                    </div>
                  )}
                </div>
                <div className="as-new-install-photos-count">
                  {uploadedPhotos.length}/3{" "}
                  {uploadedPhotos.length > 1
                    ? trans.new_install_photos_plural
                    : trans.new_install_photos_singular}
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    const remaining = 3 - uploadedPhotos.length;
                    files.slice(0, remaining).forEach((file) => {
                      const reader = new FileReader();
                      reader.onload = (ev) =>
                        setUploadedPhotos((prev) => [
                          ...prev,
                          { file, src: ev.target.result },
                        ]);
                      reader.readAsDataURL(file);
                    });
                    e.target.value = "";
                  }}
                />
                {errors.photoattach && (
                  <div className="as-error">{errors.photoattach}</div>
                )}
              </div> */}
            </div>

            <button
              className={`as-new-install-button ${warrantyInfo.custname && warrantyInfo.custtelno ? "as-green" : ""}`}
              onClick={handleSubmit}
              disabled={loading}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <CircleCheck size={20} />
                {loading
                  ? trans.new_install_submitting
                  : trans.new_install_button}
                {/* {trans.new_install_button} */}
              </div>
            </button>
          </div>
        ) : null}
      </div>

      <div className="as-new-install-footer">
        {trans.new_install_footer} <a href="tel:+60139898993">+6013-989 8993</a>
      </div>

      {showSuccessMessage && (
        <RegisteredReceived
          isNewInstall={true}
          serialQuery={buildAfterSalesQuery(searchParams)}
        />
      )}

      {showMessage && (
        <MultiPurpose
          title="Registration Status"
          message={notifMessage}
          yesText="OK"
          hideNo={true}
          onYes={() => {
            setShowMessage(false);
            window.location.reload();
          }}
          onClose={() => {
            setShowMessage(false);
            window.location.reload();
          }}
        />
      )}

      {showRegistered && (
        <RegisteredReceived
          isNewInstall={responseStatus ? true : false}
          serialQuery={buildAfterSalesQuery(searchParams)}
        />
      )}

      {showUpdatePIN && (
        <MultiPurpose
          title="Update PIN Code"
          message="The warranty is registered, are you sure you want to submit your PIN code?"
          yesText="OK"
          hideNo={true}
          onYes={() => {
            setShowUpdatePIN(false);
            handleUpdatePIN();
          }}
          onClose={() => {
            setShowUpdatePIN(false);
            navigate(`/aftersalesservice${buildAfterSalesQuery(searchParams)}`);
          }}
        />
      )}
    </AfterSalesLayout>
  );
};

export default NewInstallation;
